// src/hr/timeoff/ApprovalsPage.js -- "My team → Approvals" (mockup R2), for people who approve
// someone or are HR's backup approver. Each card shows the dates, the balance before → after, and
// who else is already out; Approve is one click, Decline asks for an optional reason in the card.
import React, { useEffect, useId, useRef, useState } from 'react';
import { FiAlertCircle, FiCheck, FiCheckCircle, FiX } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Avatar, Btn, Card, EmptyState, Field, Pill, Skeleton } from '../kit';
import { plainId } from '../kit/util';
import { daysLabel, daysNumber, rangeLabel, shortRangeLabel } from '../format';
import { dateOfTimestamp, sentAgo, shortDate } from './dates';
import { typeTone } from './labels';

const ALSO_OUT_SHOWN = 2;

function lengthOf(item) {
  if (Number(item.totalHours) === 4) return 'Half day';
  return daysLabel(item.totalHours);
}

function AlsoOut({ days }) {
  if (!days || days.length === 0) return <li>No one else out</li>;
  const shown = days.slice(0, ALSO_OUT_SHOWN);
  const rest = days.slice(ALSO_OUT_SHOWN);
  return (
    <>
      {shown.map((d) => (
        <li key={d.date} data-tone="warn">{`Also out ${shortDate(d.date)}: ${d.names.join(', ')}`}</li>
      ))}
      {rest.length > 0 && (
        <li data-tone="warn" title={rest.map((d) => `${shortDate(d.date)}: ${d.names.join(', ')}`).join('\n')}>
          {`+${rest.length} more ${rest.length === 1 ? 'day' : 'days'} with someone out`}
        </li>
      )}
    </>
  );
}

function ApprovalCard({ item, feat, delay, toneFor, onDecided }) {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null); // 'approve' | 'decline'
  const [saved, setSaved] = useState(null); // the decision, once the server has it
  const [error, setError] = useState('');
  const reasonRef = useRef(null);
  const declineRef = useRef(null);
  const wasDeclining = useRef(false);
  const nameId = `hrp-ap-${plainId(useId())}`;
  const who = item.requester || {};

  useEffect(() => {
    if (declining && reasonRef.current) reasonRef.current.focus();
    else if (!declining && wasDeclining.current && declineRef.current) declineRef.current.focus();
    wasDeclining.current = declining;
  }, [declining]);

  const decide = async (decision) => {
    setBusy(decision);
    setError('');
    const body = { decision };
    const why = reason.trim();
    if (decision === 'decline' && why) body.reason = why;
    try {
      const result = await hr.decide(item.id, body);
      // Until the list refreshes, this card says what was decided and can't be decided twice.
      setSaved(decision);
      setDeclining(false);
      onDecided({ id: item.id, decision, status: result && result.request ? result.request.status : null });
    } catch (err) {
      setError(errorText(err, "Couldn't save your decision. Try again."));
    } finally {
      setBusy(null);
    }
  };

  const hasBalance = item.availableBefore != null && item.availableAfter != null;

  return (
    <Card as="article" feat={feat} delay={delay} className="hrp-ap" aria-labelledby={nameId}>
      <Avatar id={who.id} name={who.name} size="md" className="hrp-ap__av" />
      <div className="hrp-ap__who">
        <div className="hrp-ap__l1">
          <b id={nameId}>{who.name}</b>
          {item.typeName && <Pill {...toneFor(item.typeName)} dot>{item.typeName}</Pill>}
          {item.stage === 'hr' && <Pill tone="mute">HR sign-off · you're the backup</Pill>}
          {item.createdAt && <span className="hrp-ap__sent">{`sent ${sentAgo(item.createdAt)}`}</span>}
        </div>
        <div className="hrp-ap__l2">
          {rangeLabel(item.startDate, item.endDate)}
          <span aria-hidden="true"> · </span>
          <b>{lengthOf(item)}</b>
          {item.note && <span className="hrp-ap__note">{` · “${item.note}”`}</span>}
        </div>
        <ul className="hrp-ap__facts">
          {hasBalance && (
            <li>Left to book: <b>{`${daysNumber(item.availableBefore)} → ${daysNumber(item.availableAfter)}`}</b></li>
          )}
          <AlsoOut days={item.alsoOut} />
        </ul>
        {declining && !saved && (
          <div className="hrp-ap__decline">
            <Field label="Reason" labelNote="(optional · shown to them)">
              <textarea ref={reasonRef} className="hrp-textarea" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Release week — could you move it a week later?" />
            </Field>
            <div className="hrp-ap__dbtns">
              <Btn variant="ghost" size="sm" onClick={() => setDeclining(false)} disabled={!!busy}>Keep request</Btn>
              <Btn variant="danger" size="sm" icon={FiX} loading={busy === 'decline'} onClick={() => decide('decline')}>Decline request</Btn>
            </div>
          </div>
        )}
        {error && <p className="hrp-ap__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
      </div>
      {saved && (
        <div className="hrp-ap__acts">
          <Pill tone={saved === 'approve' ? 'ok' : 'bad'} icon={saved === 'approve' ? <FiCheck /> : <FiX />}>
            {saved === 'approve' ? 'Approved' : 'Declined'}
          </Pill>
        </div>
      )}
      {!declining && !saved && (
        <div className="hrp-ap__acts">
          <Btn ref={declineRef} variant="danger" onClick={() => setDeclining(true)} disabled={!!busy}>Decline</Btn>
          <Btn variant="approve" icon={FiCheck} loading={busy === 'approve'} onClick={() => decide('approve')}>Approve</Btn>
        </div>
      )}
    </Card>
  );
}

function decidedPill(item) {
  if (item.status === 'cancelled') return { tone: 'mute', label: 'Cancelled later' };
  if (item.decision === 'decline') return { tone: 'bad', label: 'Declined' };
  if (item.status === 'approved') return { tone: 'ok', label: item.stage === 'manager' ? 'Approved · HR signed off' : 'Signed off' };
  if (item.status === 'declined') return { tone: 'bad', label: 'Approved · HR declined' };
  return { tone: 'pto', label: 'Approved · waiting on HR' };
}

function RecentlyDecided({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <Card as="section" delay={3} title="Recently decided" className="hrp-hist" aria-label="Recently decided">
      <ul className="hrp-hist__list">
        {items.map((item) => {
          const pill = decidedPill(item);
          const who = item.requester || {};
          const when = dateOfTimestamp(item.decidedAt);
          return (
            <li key={`${item.id}-${item.stage}`} className="hrp-hist__it">
              <Avatar id={who.id} name={who.name} size="sm" />
              <span className="hrp-hist__what">
                <b>{who.name}</b>
                <span>{` · ${[item.typeName, shortRangeLabel(item.startDate, item.endDate)].filter(Boolean).join(' ')}`}</span>
              </span>
              <Pill tone={pill.tone}>{pill.label}</Pill>
              {when && <span className="hrp-hist__when">{shortDate(when)}</span>}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export function ApprovalsPage({ approvals, types, onToast }) {
  const { status, data, reload } = approvals;
  // Approvals carry the type's name only; the person's own type list gives its color.
  const toneFor = (name) => typeTone((types || []).find((t) => t.name === name) || { name });
  const requests = (data && data.requests) || [];
  const decided = (data && data.decided) || [];
  // A refresh failed while a list was on screen: say so until a refresh succeeds (including while
  // "Try again" is running), and keep the list usable meanwhile.
  const [stale, setStale] = useState(false);
  useEffect(() => {
    if (status === 'error' && data) setStale(true);
    else if (status === 'ready') setStale(false);
  }, [status, data]);

  // The toast is chosen once the list has refreshed: a manager approval by someone who is also
  // HR's backup brings the same request straight back as theirs to sign off.
  const decidedOne = async ({ id, decision, status: newStatus }) => {
    const next = await reload();
    const fresh = next && next.status === 'ready' ? next.data : null;
    const backForSignOff = !!fresh && (fresh.requests || []).some((r) => r.id === id && r.stage === 'hr');
    let text = 'Request declined';
    if (decision === 'approve') {
      if (newStatus === 'approved') text = 'Approved and signed off';
      else if (backForSignOff) text = "Approved — it's now waiting on your sign-off as HR's backup";
      else text = 'Approved — sent to HR for sign-off';
    }
    onToast(text);
  };

  let sub = 'Requests from the people you approve';
  if (status === 'ready' || data) {
    sub = requests.length
      ? `${requests.length} waiting on you · approved requests go to HR for final sign-off`
      : 'Nothing waiting on you';
  }

  return (
    <div className="hrp-page hrp-apps">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">Requests to approve</h1>
          <p className="hrp-sub">{sub}</p>
        </div>
      </div>

      {!data && status !== 'error' && (
        <div className="hrp-stack">
          <Skeleton label="Loading requests" variant="card" />
          <Skeleton variant="card" />
        </div>
      )}
      {!data && status === 'error' && (
        <Card>
          <EmptyState compact icon={FiAlertCircle} title="Couldn't load your approvals" action={<Btn variant="ghost" size="sm" onClick={reload}>Try again</Btn>}>
            Something went wrong on our side.
          </EmptyState>
        </Card>
      )}
      {data && stale && (
        <div className="hrp-form-err hrp-ap__stale" role="alert">
          <FiAlertCircle aria-hidden="true" />
          <span>Your decision was saved, but this list didn't refresh.</span>
          <Btn variant="ghost" size="sm" loading={status === 'loading'} onClick={reload}>Try again</Btn>
        </div>
      )}
      {data && requests.length === 0 && (
        <Card>
          <EmptyState icon={FiCheckCircle} title="You're all caught up">
            Nothing is waiting on you. New requests show up here, and you'll get an email too.
          </EmptyState>
        </Card>
      )}
      {data && requests.length > 0 && (
        <div className="hrp-ap__list">
          {/* Keyed by step too: a request can come back at the HR step for the same person. */}
          {requests.map((item, i) => (
            <ApprovalCard key={`${item.id}-${item.stage}`} item={item} feat={i === 0} delay={i} toneFor={toneFor} onDecided={decidedOne} />
          ))}
        </div>
      )}
      {data && <RecentlyDecided items={decided} />}
    </div>
  );
}

export default ApprovalsPage;
